/**
 * Tiny fetch wrapper — injects the access token (Supabase, or Hive in Hive
 * mode) + X-Org-Id. X-Org-Id is always the *Athena* org — Hive org claims are
 * never used for Athena tenancy.
 *
 * Design: no SDK, no codegen client (until shared-types is wired up). Just
 * strongly-typed calls at the site. Every error path surfaces the server's
 * { error, ... } JSON so upgrade-required prompts can read `required_tier`.
 */
import { getHiveAccessToken, isHiveMode } from './hive';
import { getAccessToken } from './supabase';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: unknown,
    message?: string,
  ) {
    super(message ?? `${status}`);
  }
}

type Options = RequestInit & {
  orgId?: string;
  skipAuth?: boolean;
};

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  headers.set('content-type', headers.get('content-type') ?? 'application/json');

  if (!opts.skipAuth) {
    const token = isHiveMode() ? await getHiveAccessToken() : await getAccessToken();
    if (token) headers.set('authorization', `Bearer ${token}`);
  }
  if (opts.orgId) headers.set('x-org-id', opts.orgId);

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const res = await fetch(url, { ...opts, headers });

  const text = await res.text();
  const body = text ? (safeParseJson(text) ?? text) : null;

  if (!res.ok) {
    const detail = typeof body === 'object' && body !== null ? body : { error: body };
    throw new ApiError(res.status, detail, typeof body === 'string' ? body : undefined);
  }
  return body as T;
}

function safeParseJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}
